import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Check, Crown, Zap, ArrowLeft, Loader2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_URL = `${BACKEND_URL}/api`;

const PlansPage = () => {
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [upgrading, setUpgrading] = useState(null);

  useEffect(() => {
    loadPlans();
    
    // Registrar listener para atualizações em tempo real
    import('../services/plansSyncService').then(({ default: plansSyncService }) => {
      const unsubscribe = plansSyncService.addListener((plansConfig) => {
        console.log('🔄 PlansPage: Atualizando planos...');
        loadPlans(); // Recarregar planos
      });
      
      return () => unsubscribe();
    });
  }, []);

  const loadPlans = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Carregar configurações dinâmicas dos planos
      const configResponse = await axios.get(`${API_URL}/plans/config`);
      const plansConfig = configResponse.data.plans || {};
      
      // Carregar dados do usuário se estiver logado
      let userPlan = null;
      let worksCount = 0;
      
      if (token) {
        try {
          const userResponse = await axios.get(`${API_URL}/plans`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          const userPlans = userResponse.data;
          userPlan = userPlans.find(p => p.current);
          worksCount = userPlan?.works_count || 0;
        } catch (err) {
          console.log('User not logged in or token expired');
        }
      }
      
      // Montar array de planos com configurações dinâmicas
      const formattedPlans = [];
      
      // Plano Individual
      if (plansConfig.individual) {
        formattedPlans.push({
          name: plansConfig.individual.name || 'Plano Individual',
          price: plansConfig.individual.price || 0,
          max_works: plansConfig.individual.music_limit || 10,
          duration_months: plansConfig.individual.duration_months || 0,
          current: userPlan?.name?.toLowerCase().includes('individual'),
          works_count: userPlan?.name?.toLowerCase().includes('individual') ? worksCount : 0
        });
      }
      
      // Plano Professional
      if (plansConfig.professional) {
        formattedPlans.push({
          name: plansConfig.professional.name || 'Plano Professional',
          price: plansConfig.professional.price || 99.90,
          max_works: plansConfig.professional.music_limit || 50,
          duration_months: plansConfig.professional.duration_months || 6,
          current: userPlan?.name?.toLowerCase().includes('professional'),
          works_count: userPlan?.name?.toLowerCase().includes('professional') ? worksCount : 0
        });
      }
      
      // Plano Premium
      if (plansConfig.premium) {
        formattedPlans.push({
          name: plansConfig.premium.name || 'Plano Premium',
          price: plansConfig.premium.price || 299.90,
          max_works: plansConfig.premium.music_limit || -1,
          duration_months: plansConfig.premium.duration_months || 6,
          current: userPlan?.name?.toLowerCase().includes('premium'),
          works_count: userPlan?.name?.toLowerCase().includes('premium') ? worksCount : 0
        });
      }
      
      // Plano Annual
      if (plansConfig.annual) {
        formattedPlans.push({
          name: plansConfig.annual.name || 'Plano Anual',
          price: plansConfig.annual.price || 299.90,
          max_works: plansConfig.annual.music_limit || -1,
          duration_months: plansConfig.annual.duration_months || 12,
          current: userPlan?.name?.toLowerCase().includes('annual'),
          works_count: userPlan?.name?.toLowerCase().includes('annual') ? worksCount : 0,
          popular: true // Marcar como popular/destaque
        });
      }
      
      setPlans(formattedPlans);
    } catch (error) {
      console.error('Error loading plans:', error);
      toast.error('Erro ao carregar planos');
    } finally {
      setLoading(false);
    }
  };

  const handleUpgrade = async (planId) => {
    try {
      setUpgrading(planId);
      
      // Se for plano gratuito, fazer upgrade direto
      if (planId === 'individual') {
        const token = localStorage.getItem('token');
        await axios.post(`${API_URL}/plans/upgrade`, 
          { new_plan: planId },
          { headers: { Authorization: `Bearer ${token}` }}
        );
        toast.success('Plano atualizado com sucesso!');
        await loadPlans();
        setTimeout(() => navigate('/dashboard'), 2000);
      } else {
        // Para planos pagos, redirecionar para página de pagamento
        toast.info('Redirecionando para pagamento...');
        setTimeout(() => navigate(`/payment/plan/${planId}`), 1000);
      }
    } catch (error) {
      console.error('Error upgrading plan:', error);
      toast.error('Erro ao processar upgrade');
    } finally {
      setUpgrading(null);
    }
  };

  const getPlanIcon = (planName) => {
    if (planName.includes('Premium')) return Crown;
    if (planName.includes('Profissional')) return Zap;
    return Check;
  };

  const getPlanColor = (planName) => {
    if (planName.includes('Premium')) return 'from-purple-500 to-pink-600';
    if (planName.includes('Profissional')) return 'from-blue-500 to-indigo-600';
    return 'from-emerald-500 to-teal-600'; // Mudado de cinza para verde/teal vibrante
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center">
        <Loader2 className="w-12 h-12 text-blue-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 py-12 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Back Button */}
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-2 text-gray-600 hover:text-blue-600 transition-colors mb-8 bg-white hover:bg-gray-50 px-4 py-2 rounded-lg shadow-sm"
        >
          <ArrowLeft className="w-5 h-5" />
          <span className="font-semibold">Voltar</span>
        </button>

        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-5xl font-bold text-gray-900 mb-4">
            Escolha Seu Plano
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-6">
            Proteja suas obras musicais e garanta seus direitos autorais com certificados digitais seguros
          </p>
          
          {/* Aviso de Preços Promocionais */}
          <div className="inline-flex items-center gap-2 bg-gradient-to-r from-red-500 to-pink-500 text-white px-6 py-3 rounded-full shadow-lg animate-pulse">
            <span className="text-2xl">🔥</span>
            <span className="font-bold text-lg">PREÇOS PROMOCIONAIS POR TEMPO LIMITADO!</span>
            <span className="text-2xl">🔥</span>
          </div>
        </div>

        {/* Plans Grid */}
        <div className="grid md:grid-cols-3 gap-8 mb-12">
          {plans.map((plan) => {
            const Icon = getPlanIcon(plan.name);
            const colorGradient = getPlanColor(plan.name);
            const planId = plan.name.toLowerCase().replace(/\s/g, '_').replace('profissional', 'professional').replace('premium_ilimitado', 'premium').replace('individual', 'individual');
            const isCurrentPlan = plan.current;
            const isPremium = plan.name.includes('Premium');
            const isUpgradingThis = upgrading === planId;

            return (
              <div
                key={plan.name}
                className={`relative bg-white rounded-2xl shadow-xl overflow-hidden transition-all hover:shadow-2xl ${
                  isPremium ? 'ring-4 ring-purple-500 ring-opacity-50 transform scale-105' : ''
                }`}
              >
                {/* Badge Promoção no canto superior esquerdo */}
                <div className="absolute top-4 left-4 z-10">
                  <div className="bg-gradient-to-r from-red-500 to-pink-500 text-white px-3 py-1 rounded-full text-xs font-bold shadow-md transform -rotate-12">
                    PROMOÇÃO
                  </div>
                </div>
                
                {/* Premium Badge */}
                {isPremium && (
                  <div className="absolute top-0 right-0 bg-gradient-to-r from-purple-600 to-pink-600 text-white px-4 py-1 text-sm font-bold">
                    MAIS POPULAR
                  </div>
                )}

                {/* Current Plan Badge */}
                {isCurrentPlan && (
                  <div className="absolute top-4 right-4 bg-green-500 text-white px-3 py-1 rounded-full text-xs font-bold z-10">
                    PLANO ATUAL
                  </div>
                )}

                {/* Header */}
                <div className={`bg-gradient-to-r ${colorGradient} p-8 text-white`}>
                  <Icon className="w-12 h-12 mb-4" />
                  <h3 className="text-2xl font-bold mb-2">{plan.name}</h3>
                  <div className="flex items-baseline gap-2">
                    <span className="text-4xl font-bold">
                      {plan.price === 0 ? 'GRÁTIS' : `R$ ${plan.price.toFixed(2)}`}
                    </span>
                    {plan.price > 0 && <span className="text-sm opacity-75">/mês</span>}
                  </div>
                </div>

                {/* Features */}
                <div className="p-8">
                  <div className="mb-6">
                    <div className="flex items-center gap-2 mb-4">
                      <Check className="w-5 h-5 text-green-500" />
                      <span className="font-semibold">
                        {plan.max_works === -1 ? 'Obras Ilimitadas' : `Até ${plan.max_works} obras`}
                      </span>
                    </div>
                    {isCurrentPlan && (
                      <div className="bg-blue-50 p-3 rounded-lg">
                        <p className="text-sm text-blue-800">
                          <span className="font-bold">{plan.works_count}</span> de{' '}
                          <span className="font-bold">
                            {plan.max_works === -1 ? '∞' : plan.max_works}
                          </span>{' '}
                          obras utilizadas
                        </p>
                      </div>
                    )}
                  </div>

                  <ul className="space-y-3 mb-8">
                    <li className="flex items-start gap-2">
                      <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-600">Certificados digitais com blockchain</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-600">Upload de arquivos (áudio, letra, partitura)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-600">Registro de coautores</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-600">Verificação pública de certificados</span>
                    </li>
                    {isPremium && (
                      <>
                        <li className="flex items-start gap-2">
                          <Check className="w-5 h-5 text-purple-500 flex-shrink-0 mt-0.5" />
                          <span className="text-gray-600 font-semibold">Suporte prioritário</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <Check className="w-5 h-5 text-purple-500 flex-shrink-0 mt-0.5" />
                          <span className="text-gray-600 font-semibold">API de integração</span>
                        </li>
                      </>
                    )}
                  </ul>

                  {/* CTA Button */}
                  {isCurrentPlan ? (
                    <button
                      disabled
                      className="w-full py-3 bg-gray-200 text-gray-500 rounded-xl font-semibold cursor-not-allowed"
                    >
                      Plano Atual
                    </button>
                  ) : (
                    <button
                      onClick={() => handleUpgrade(planId)}
                      disabled={isUpgradingThis}
                      className={`w-full py-3 bg-gradient-to-r ${colorGradient} text-white rounded-xl font-semibold hover:shadow-lg transition-all disabled:opacity-50`}
                    >
                      {isUpgradingThis ? (
                        <span className="flex items-center justify-center gap-2">
                          <Loader2 className="w-5 h-5 animate-spin" />
                          Atualizando...
                        </span>
                      ) : (
                        'Escolher Plano'
                      )}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Info Box */}
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 text-center max-w-3xl mx-auto">
          <h3 className="font-bold text-blue-900 mb-2">📝 Cadastro Totalmente Gratuito!</h3>
          <p className="text-blue-700">
            Comece agora mesmo com o plano Individual gratuito e proteja até 5 obras musicais. 
            Faça upgrade a qualquer momento para aumentar seu limite.
          </p>
        </div>
      </div>
    </div>
  );
};

export default PlansPage;
